import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sparkle, List, X } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/patent-services", label: "Patent and Trademark Services" },
  { to: "/ip-training", label: "IP Training" },
  { to: "/blog", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-[background-color,border-color] duration-500 ${
        scrolled || open
          ? "backdrop-blur-xl bg-[#061324]/85 border-b border-[#C6A87C]/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 xl:px-24 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <span className="w-9 h-9 border border-[#C6A87C]/60 text-[#C6A87C] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#C6A87C] group-hover:text-[#061324]">
            <Sparkle weight="fill" size={16} />
          </span>
          <div className="leading-none">
            <div className="font-serif text-xl text-[#F5F4F0] tracking-tight">
              Gurukul <span className="gold">of Ramana</span>
            </div>
            <div className="mt-1 text-[9px] font-sans tracking-[0.22em] uppercase font-semibold text-[#8A99AA]">
              Patent &amp; Trademark Services · IP Training
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              end={n.to === "/"}
              className={({ isActive }) =>
                `relative text-[13px] tracking-[0.08em] uppercase transition-colors duration-300 ${
                  isActive
                    ? "text-[#C6A87C]"
                    : "text-[#8A99AA] hover:text-[#F5F4F0]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute -bottom-2 left-0 right-0 h-px bg-[#C6A87C]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 lg:ml-8">
          <Link
            to="/enroll"
            data-testid="header-cta-enroll"
            className="group relative inline-flex items-center h-11 px-6 bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.14em] uppercase overflow-hidden"
          >
            <span className="absolute inset-0 bg-[#14B981] scale-x-0 origin-left transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
            <span className="relative">Book a Free Call</span>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-[#F5F4F0]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#061324]/95 backdrop-blur-xl border-t border-[#C6A87C]/15 min-h-[calc(100dvh-5rem)]"
          >
            <div className="px-6 py-10 space-y-6">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className="block text-lg font-serif text-[#F5F4F0]/85 hover:text-[#C6A87C] transition-colors"
                >
                  {n.label}
                </NavLink>
              ))}
              <Link to="/enroll" data-testid="mobile-cta-enroll" className="block pt-2">
                <span className="flex items-center justify-center h-12 w-full bg-[#0F9D6B] text-[#F5F4F0] text-xs font-semibold tracking-[0.14em] uppercase">
                  Book a Free Call
                </span>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
